// pages/secure/DocumentLibraryPage.js
// The required-document library: view and print the Illinois disclosures and agency
// documents. Managing broker can add/edit. Printing opens a clean, form-only view.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';

const DocumentLibraryPage = () => {
    const apiClient = useApiClient();
    const { user } = useAuth();
    const isMB = !!user?.isManagingBroker;

    const [docs, setDocs] = useState([]);
    const [error, setError] = useState('');
    const [viewing, setViewing] = useState(null);
    const [editing, setEditing] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(() => {
        apiClient.get('/api/document-library').then(setDocs).catch(err => setError(err.message));
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);

    const open = async (id) => {
        try { setViewing(await apiClient.get(`/api/document-library/${id}`)); }
        catch (err) { setError(err.message); }
    };

    // Print just the document content in a new window.
    const printDoc = (doc) => {
        const w = window.open('', '_blank');
        w.document.write(`<html><head><title>${doc.title}</title>
            <style>body{margin:40px;} @page{margin:1in;}</style></head>
            <body>${doc.content}</body></html>`);
        w.document.close();
        w.focus();
        setTimeout(() => { w.print(); }, 300);
    };

    const saveEdit = async () => {
        setIsSaving(true); setError('');
        try {
            if (editing.template_id) await apiClient.put(`/api/document-library/${editing.template_id}`, editing);
            else await apiClient.post('/api/document-library', editing);
            setEditing(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Remove "${doc.title}" from the library?`)) return;
        try { await apiClient.delete(`/api/document-library/${doc.template_id}`); load(); }
        catch (err) { setError(err.message); }
    };

    return (
        <div className="p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Document Library</h1>
                {isMB && <button onClick={() => setEditing({ doc_key: '', title: '', category: 'Disclosures', statutory_ref: '', is_required: true, retention_years: 5, content: '' })}
                                 className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ Add document</button>}
            </div>
            <p className="text-slate-500 mb-6">Required Illinois disclosures and agency documents — view, print, and keep on file. Records are retained 5 years (68 IAC 1450.755).</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            <div className="bg-white rounded-lg shadow p-3">
                <DataList rows={docs} rowKey="template_id" csvName="document-library" defaultSort={{ key: 'category', dir: 'asc' }}
                    empty="No documents in the library yet." filters={[{ key: 'category', label: 'Category' }, { key: 'required', label: 'Required' }]}
                    columns={[
                        { key: 'category', label: 'Category' },
                        { key: 'title', label: 'Document', render: (d) => <span className="font-medium text-slate-700">{d.title}</span> },
                        { key: 'required', label: 'Required', value: (d) => (Number(d.is_required) === 1 ? 'required' : ''),
                            render: (d) => (Number(d.is_required) === 1 ? <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">required</span> : null) },
                        { key: 'statutory_ref', label: 'Statutory reference', render: (d) => <span className="text-xs text-slate-500">{d.statutory_ref}</span> },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (d) => (
                            <span className="flex gap-3 whitespace-nowrap">
                                <button onClick={() => open(d.template_id)} className="text-blue-600 hover:underline text-sm">View</button>
                                <button onClick={async () => { try { printDoc(await apiClient.get(`/api/document-library/${d.template_id}`)); } catch (err) { setError(err.message); } }}
                                        className="text-slate-500 hover:underline text-sm">Print</button>
                                {isMB && <>
                                    <button onClick={async () => setEditing(await apiClient.get(`/api/document-library/${d.template_id}`))} className="text-slate-500 hover:underline text-sm">Edit</button>
                                    <button onClick={() => remove(d)} className="text-red-500 hover:underline text-sm">×</button>
                                </>}
                            </span>) },
                    ]} />
            </div>

            {/* View modal */}
            <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.title || ''} size="lg"
                   footer={viewing && <button onClick={() => printDoc(viewing)} className="bg-slate-700 text-white px-4 py-2 rounded-md hover:bg-slate-800">🖨 Print</button>}>
                {viewing && <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: viewing.content }} />}
            </Modal>

            {/* Edit modal (MB) */}
            <Modal open={!!editing} onClose={() => setEditing(null)} size="lg"
                   title={editing?.template_id ? 'Edit document' : 'Add document'}
                   footer={<>
                       <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={saveEdit} disabled={isSaving || !editing?.title?.trim() || !editing?.doc_key?.trim()}
                               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
                   </>}>
                {editing && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Doc key</label>
                                <input value={editing.doc_key} disabled={!!editing.template_id} onChange={e => setEditing(p => ({ ...p, doc_key: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="e.g. mold_disclosure" /></div>
                            <div><label className="block text-sm font-medium text-slate-700">Category</label>
                                <input value={editing.category} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        </div>
                        <div><label className="block text-sm font-medium text-slate-700">Title</label>
                            <input value={editing.title} onChange={e => setEditing(p => ({ ...p, title: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Statutory reference</label>
                                <input value={editing.statutory_ref || ''} onChange={e => setEditing(p => ({ ...p, statutory_ref: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                            <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox" checked={!!editing.is_required} onChange={e => setEditing(p => ({ ...p, is_required: e.target.checked }))} /> Required document</label></div>
                        </div>
                        <div><label className="block text-sm font-medium text-slate-700">Content (HTML — printable)</label>
                            <textarea value={editing.content || ''} rows="16" onChange={e => setEditing(p => ({ ...p, content: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2 font-mono text-xs" /></div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DocumentLibraryPage;
